import React from 'react';
import { Upload, Edit, Rocket } from 'lucide-react';
import { howItWorks } from '../mock';
import demoVideo from '../assets/videos/an_you_create_an_animated_vide.mp4';

const iconMap = {
  Upload: Upload,
  Edit: Edit,
  Rocket: Rocket
};

export const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-24 bg-gradient-to-b from-black to-gray-950 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center mb-20 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Three steps to <span className="text-gradient">success</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            From product photo to marketplace listing in minutes, not hours
          </p>
        </div>

        <div className="relative">
          {/* Connection lines */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 via-yellow-400 to-yellow-500 transform -translate-y-1/2 opacity-20"></div>

          <div className="grid md:grid-cols-3 gap-12 lg:gap-8 relative">
            {howItWorks.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              return (
                <div
                  key={step.step}
                  className={`relative animate-fade-in-up stagger-${index + 1}`}
                >
                  {/* Step card */}
                  <div className="glass rounded-3xl p-8 text-center hover:scale-105 transition-transform duration-300">
                    {/* Step number badge */}
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                      <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xl shadow-lg">
                        {step.step}
                      </div>
                    </div>

                    {/* Icon */}
                    <div className="mt-8 mb-6 flex justify-center">
                      <div className="w-20 h-20 bg-yellow-500/10 rounded-2xl flex items-center justify-center">
                        <IconComponent className="w-10 h-10 text-yellow-500" />
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-white mb-4">{step.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{step.description}</p>
                  </div>

                  {/* Arrow for larger screens */}
                  {index < howItWorks.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-12 transform -translate-y-1/2 z-10">
                      <div className="w-8 h-8 border-t-2 border-r-2 border-yellow-500 transform rotate-45"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Demo Section */}
        <div className="mt-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1 animate-fade-in-up stagger-4">
            <h3 className="text-3xl font-bold text-white mb-6">See it in action</h3>
            <p className="text-gray-400 text-lg mb-6 leading-relaxed">
              Watch how Snapcom transforms a simple product photo into a complete, marketplace-ready listing with accurate descriptions, competitive pricing, and high-quality images.
            </p>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <p className="text-gray-300">AI identifies product from your photo in seconds</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <p className="text-gray-300">Fetches data from multiple marketplaces automatically</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <p className="text-gray-300">Customize details and publish with one click</p>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 animate-fade-in-up stagger-5">
            <div className="relative">
              <video
                src={demoVideo}
                className="rounded-2xl shadow-2xl w-full h-auto"
                controls
                autoPlay
                loop
                muted
                playsInline
                poster=""
              >
                Your browser does not support the video tag.
              </video>
              <div className="absolute inset-0 bg-yellow-500/5 rounded-2xl pointer-events-none"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};